<template>
  <div class="page flex-col category-tool-page">
    <div class="group_1 flex-col">
      <div class="box_1 flex-col justify-end">
        <site-header @auth-updated="handleAuthUpdated" />
        <div class="group_3 flex-col workspace-side-rail-content">
          <div class="new-container">
            <div class="box_5 flex-row">
              <div class="rag-main">
                <div class="category-layout">
                  <main class="category-tool">
                    <el-upload
                      ref="upload"
                      class="excel-upload"
                      drag
                      :show-file-list="false"
                      :limit="1"
                      :disabled="uploading"
                      action="#"
                      accept=".xls,.xlsx"
                      :on-change="onChangeFile"
                      :auto-upload="false"
                    >
                      <div class="upload-box-content">
                        <span class="upload-box-icon">
                          <i :class="uploading ? 'el-icon-loading' : 'el-icon-upload2'"></i>
                        </span>
                        <span class="upload-box-copy">
                          <strong>{{ uploading ? '正在解析 Excel…' : (fileInfo ? '点击或拖拽上传新文件' : '上传 Excel 文件') }}</strong>
                          <small>支持 .xls / .xlsx 格式，单个文件不超过 20MB</small>
                        </span>
                        <span class="upload-box-action">{{ fileInfo ? '重新选择' : '选择文件' }}</span>
                      </div>
                    </el-upload>

                    <div class="toolbar">
                      <el-button
                        v-if="canStart"
                        type="primary"
                        icon="el-icon-video-play"
                        :loading="creatingTask"
                        @click="playPrediction"
                      >{{ startButtonText }}</el-button>
                      <el-button v-if="currentTask && isActiveStatus(currentTask.status)" icon="el-icon-video-pause" @click="pausePrediction">暂停</el-button>

                      <el-select v-if="columns.length" v-model="selColnum" class="toolbar-select" placeholder="预测字段">
                        <el-option v-for="item in columns" :key="item" :label="item" :value="item" />
                      </el-select>
                      <el-select v-if="columns.length" v-model="selCategoryLv" class="toolbar-select" placeholder="分类数量">
                        <el-option v-for="item in categoryLen" :key="item.value" :label="item.label" :value="item.value" />
                      </el-select>
                      <span v-if="columns.length" class="hierarchy-chip">输出一级 / 二级 / 三级</span>

                      <div class="quota-chip" :class="{ unlimited: quota.unlimited }">
                        <i class="el-icon-coin"></i>
                        <span v-if="quota.unlimited">正式机构 · 不限额度</span>
                        <span v-else-if="quotaLoaded">剩余 {{ quota.remaining }}/{{ quota.limit }} 条</span>
                        <span v-else>登录后查看额度</span>
                      </div>

                      <div class="button-right">
                        <el-button icon="el-icon-download" :disabled="!canExport" :loading="exporting" @click="exportToExcel">导出</el-button>
                        <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
                      </div>
                    </div>

                    <div v-if="fileInfo" class="info">
                      <div class="info-item">
                        <i class="el-icon-document"></i>
                        <span :title="fileInfo.fileName">文件名：{{ fileInfo.fileName }}</span>
                      </div>
                      <div class="info-progress">
                        <span>{{ currentTask ? statusText(currentTask.status) : '文件预览' }}</span>
                        <span class="curr-count">{{ processedRows }}/{{ totalRows }}</span>
                      </div>
                      <div class="info-progress-bar"><div class="bar" :style="{ width: progressPercent + '%' }"></div></div>
                      <el-button
                        v-if="currentTask && ['QUEUED', 'RUNNING', 'PAUSED'].includes(currentTask.status)"
                        type="text"
                        @click="cancelPrediction"
                      >取消任务</el-button>
                    </div>

                    <div class="table-wrap">
                      <div v-if="!fileInfo && !rowsLoading" class="empty-state">
                        <i class="el-icon-s-grid"></i>
                        <h3>上传 Excel 开始分类预测</h3>
                        <p>服务端将解析文件、检查免费额度并在后台执行分类</p>
                      </div>
                      <el-table v-else v-loading="rowsLoading || uploading" :data="showList" class="result-table" stripe>
                        <el-table-column label="Excel行" align="center" width="82">
                          <template slot-scope="scope">{{ scope.row.rowNo }}</template>
                        </el-table-column>
                        <el-table-column :label="selColnum || '内容'" min-width="300" show-overflow-tooltip>
                          <template slot-scope="scope"><span class="cell-text">{{ scope.row[selColnum] || scope.row.sourceText || '—' }}</span></template>
                        </el-table-column>
                        <el-table-column label="最终分类" min-width="320" show-overflow-tooltip>
                          <template slot-scope="scope">
                            <div class="category-result-cell">
                              <span :class="['cell-text', !scope.row.category && 'is-empty']">{{ rowCategory(scope.row) }}</span>
                              <small v-if="scope.row.decisionSource" :class="['decision-source', decisionClass(scope.row.decisionSource)]">{{ decisionText(scope.row.decisionSource) }}</small>
                            </div>
                          </template>
                        </el-table-column>
                        <el-table-column label="机标一级" min-width="180" show-overflow-tooltip>
                          <template slot-scope="scope"><span class="cell-text secondary-result">{{ scope.row.firstL1 || '—' }}</span></template>
                        </el-table-column>
                        <el-table-column label="AI复核一级" min-width="180" show-overflow-tooltip>
                          <template slot-scope="scope"><span class="cell-text secondary-result">{{ scope.row.aiL1 || '—' }}</span></template>
                        </el-table-column>
                        <el-table-column label="状态" align="center" width="100">
                          <template slot-scope="scope"><span class="row-status" :class="statusClass(scope.row.status)">{{ rowStatusText(scope.row.status) }}</span></template>
                        </el-table-column>
                      </el-table>
                    </div>

                    <div v-if="currentTask && rowTotal > 0" class="pagination-wrap">
                      <el-pagination
                        :current-page="page"
                        :page-size="size"
                        :page-sizes="[10, 20, 50, 100]"
                        :total="rowTotal"
                        layout="total, sizes, prev, pager, next, jumper"
                        @size-change="handlePageSizeChange"
                        @current-change="handlePageChange"
                      />
                    </div>
                  </main>

                  <aside class="history-panel" aria-label="历史上传文件">
                    <div class="history-scroll" @scroll="handleHistoryScroll">
                      <div v-if="historyLoading && !history.length" class="history-state"><i class="el-icon-loading"></i><span>正在载入</span></div>
                      <div v-else-if="!hasLogin" class="history-state"><i class="el-icon-user"></i><span>登录后查看历史记录</span></div>
                      <div v-else-if="!history.length" class="history-state"><i class="el-icon-document"></i><span>暂无上传记录</span></div>
                      <button
                        v-for="task in history"
                        :key="task.taskId"
                        type="button"
                        class="history-item"
                        :class="{ active: currentTask && currentTask.taskId === task.taskId }"
                        @click="openHistoryTask(task)"
                      >
                        <span class="history-thumbnail">
                          <i class="el-icon-document"></i>
                          <span v-if="isActiveStatus(task.status)" class="thumbnail-progress"><span :style="{ width: Math.max(6, task.progress || 0) + '%' }"></span></span>
                        </span>
                        <span class="history-info">
                          <strong :title="task.fileName">{{ task.fileName }}</strong>
                          <span class="history-status" :class="statusClass(task.status)">{{ historySummary(task) }}</span>
                          <time>{{ task.createTime }}</time>
                        </span>
                      </button>
                      <div v-if="historyLoading && history.length" class="history-more"><i class="el-icon-loading"></i></div>
                      <div v-else-if="history.length && history.length >= historyTotal" class="history-end">已加载全部记录</div>
                    </div>
                  </aside>
                </div>
              </div>
            </div>
          </div>
        </div>
        <site-footer class="translate-site-footer" />
      </div>
    </div>
    <floating-contact-tools />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import FloatingContactTools from '@/components/home/FloatingContactTools.vue'
import { categoryPredictServiceClient, categoryPredictStreamClient } from '@/tools/api/service'
import {
  CategoryQuotaRequest,
  CategoryUploadRequest,
  CategoryCreateTaskRequest,
  CategoryTaskPageRequest,
  CategoryTaskRequest,
  CategoryTaskRowsRequest
} from '../../api/categoryPredictService_pb'

const ACTIVE_STATUSES = ['QUEUED', 'RUNNING']

export default {
  name: 'CategoryToolServer',
  components: { SiteHeader, SiteFooter, FloatingContactTools },
  data () {
    return {
      authStateVersion: 0,
      uploading: false,
      creatingTask: false,
      exporting: false,
      rowsLoading: false,
      historyLoading: false,
      quotaLoaded: false,
      quota: { tier: '', limit: 0, used: 0, reserved: 0, remaining: 0, unlimited: false },
      uploadId: '',
      fileInfo: null,
      currentTask: null,
      columns: [],
      selColnum: '',
      selCategoryLv: 1,
      categoryLen: [{ label: '占比第一', value: 1 }, { label: '占比前二', value: 2 }],
      showList: [],
      rowTotal: 0,
      page: 1,
      size: 20,
      history: [],
      historyPage: 1,
      historyPageSize: 20,
      historyTotal: 0,
      pollTimer: null,
      activeRequestId: 0
    }
  },
  computed: {
    hasLogin () { return this.authStateVersion >= 0 && Boolean(localStorage.getItem('token')) },
    canStart () { return Boolean(this.uploadId) || (this.currentTask && this.currentTask.status === 'PAUSED') },
    startButtonText () { return this.currentTask && this.currentTask.status === 'PAUSED' ? '继续' : '预测' },
    canExport () { return this.currentTask && Number(this.currentTask.processedRows || 0) > 0 },
    totalRows () { return this.currentTask ? Number(this.currentTask.totalRows || 0) : Number((this.fileInfo && this.fileInfo.rowCount) || 0) },
    processedRows () { return this.currentTask ? Number(this.currentTask.processedRows || 0) : 0 },
    progressPercent () {
      if (!this.totalRows) return 0
      return this.currentTask ? Math.min(100, Math.round(this.processedRows * 100 / this.totalRows)) : 0
    }
  },
  created () {
    if (this.hasLogin) this.refreshAuthenticatedData(true)
  },
  beforeDestroy () { this.stopPolling() },
  methods: {
    handleAuthUpdated () { this.authStateVersion += 1; if (this.hasLogin) this.refreshAuthenticatedData(true) },
    requireLogin () { if (this.hasLogin) return true; this.openLogin(); return false },
    openLogin () { if (this.$authPopup) this.$authPopup.open(process.env.VUE_APP_WEB_LOGIN_URL || '/login'); else this.$router.push('/login') },
    async refreshAuthenticatedData (openLatest) { await Promise.all([this.loadQuota(), this.loadHistory(true, openLatest)]) },
    ensureResponse (response) { if (!response || !response.getStatus()) throw new Error(response ? response.getMessage() : '请求失败'); return response },
    async loadQuota () {
      if (!this.hasLogin) return
      try {
        const response = this.ensureResponse(await categoryPredictServiceClient.getQuota(new CategoryQuotaRequest(), this.grpcMetadata()))
        this.quota = { tier: response.getTier(), limit: response.getLimit(), used: response.getUsed(), reserved: response.getReserved(), remaining: response.getRemaining(), unlimited: response.getUnlimited() }
        this.quotaLoaded = true
      } catch (e) { this.handleError(e, '额度信息加载失败', false) }
    },
    async onChangeFile (file) {
      if (!this.requireLogin()) { this.clearUploadFiles(); return }
      const raw = file && file.raw
      if (!raw) return
      const extension = String(raw.name || '').split('.').pop().toLowerCase()
      if (!['xls', 'xlsx'].includes(extension)) { this.$message.warning('仅支持 .xls 和 .xlsx 文件'); this.clearUploadFiles(); return }
      if (!raw.size || raw.size > 20 * 1024 * 1024) { this.$message.warning('文件大小需在20MB以内'); this.clearUploadFiles(); return }
      this.stopPolling()
      this.uploading = true
      try {
        const request = new CategoryUploadRequest()
        request.setFile(new Uint8Array(await this.readFile(raw)))
        request.setFileName(raw.name)
        request.setFileSize(raw.size)
        const response = this.ensureResponse(await categoryPredictServiceClient.uploadPreview(request, this.grpcMetadata()))
        this.currentTask = null
        this.uploadId = response.getUploadId()
        this.fileInfo = { fileName: response.getFileName(), fileSize: response.getFileSize(), rowCount: response.getRowCount() }
        this.columns = response.getColumnsList()
        this.selColnum = this.columns[0] || ''
        this.showList = response.getPreviewRowsList().map(item => this.previewRow(item))
        this.rowTotal = this.showList.length
        this.page = 1
        this.$message.success('Excel解析完成，请选择预测字段')
      } catch (e) { this.handleError(e, 'Excel上传失败') } finally { this.uploading = false; this.clearUploadFiles() }
    },
    async playPrediction () {
      if (!this.requireLogin()) return
      if (this.currentTask && this.currentTask.status === 'PAUSED') { await this.operateTask('resumeTask'); return }
      if (!this.uploadId || !this.selColnum) { this.$message.warning('请先上传Excel并选择预测字段'); return }
      this.creatingTask = true
      try {
        const request = new CategoryCreateTaskRequest()
        request.setUploadId(this.uploadId)
        request.setTargetColumn(this.selColnum)
        request.setTopCategoryCount(this.selCategoryLv)
        request.setMaxLevel(2)
        request.setIdempotencyKey(this.uuid())
        const response = this.ensureResponse(await categoryPredictServiceClient.createTask(request, this.grpcMetadata()))
        const task = this.taskObject(response.getTask())
        this.currentTask = task
        this.uploadId = ''
        this.upsertHistory(task, true)
        this.page = 1
        await this.loadRows()
        this.startPolling()
        await this.loadQuota()
        this.$message.success('任务已创建，正在后台分类')
      } catch (e) { this.handleError(e, '任务创建失败') } finally { this.creatingTask = false }
    },
    async pausePrediction () { await this.operateTask('pauseTask') },
    async cancelPrediction () { await this.operateTask('cancelTask') },
    async operateTask (method) {
      if (!this.currentTask) return
      const request = new CategoryTaskRequest(); request.setTaskId(this.currentTask.taskId)
      try {
        const response = this.ensureResponse(await categoryPredictServiceClient[method](request, this.grpcMetadata()))
        this.currentTask = this.taskObject(response.getTask())
        this.upsertHistory(this.currentTask)
        if (method === 'resumeTask') this.startPolling(); else this.stopPolling()
        await this.loadQuota()
      } catch (e) { this.handleError(e, '任务操作失败') }
    },
    async loadHistory (reset, openLatest) {
      if (!this.hasLogin || this.historyLoading) return
      if (reset) this.historyPage = 1
      if (!reset && this.history.length >= this.historyTotal) return
      this.historyLoading = true
      try {
        const request = new CategoryTaskPageRequest(); request.setCurrent(this.historyPage); request.setSize(this.historyPageSize)
        const response = this.ensureResponse(await categoryPredictServiceClient.pageTasks(request, this.grpcMetadata()))
        const records = response.getRecordsList().map(this.taskObject)
        this.history = reset ? records : this.history.concat(records.filter(item => !this.history.some(old => old.taskId === item.taskId)))
        this.historyTotal = Number(response.getTotal())
        if (openLatest && !this.currentTask && !this.uploadId && this.history.length) await this.openHistoryTask(this.history[0])
      } catch (e) { this.handleError(e, '历史记录加载失败', false) } finally { this.historyLoading = false }
    },
    handleHistoryScroll (event) {
      const target = event.target
      if (target.scrollTop + target.clientHeight < target.scrollHeight - 48 || this.historyLoading || this.history.length >= this.historyTotal) return
      this.historyPage += 1; this.loadHistory(false, false)
    },
    async openHistoryTask (summary) {
      this.stopPolling(); const requestId = ++this.activeRequestId
      try {
        const request = new CategoryTaskRequest(); request.setTaskId(summary.taskId)
        const response = this.ensureResponse(await categoryPredictServiceClient.getTask(request, this.grpcMetadata()))
        if (requestId !== this.activeRequestId) return
        this.currentTask = this.taskObject(response.getTask())
        this.uploadId = ''
        this.fileInfo = { fileName: this.currentTask.fileName, fileSize: this.currentTask.fileSize, rowCount: this.currentTask.totalRows }
        this.columns = this.currentTask.columns
        this.selColnum = this.currentTask.targetColumn
        this.selCategoryLv = this.currentTask.topCategoryCount
        this.page = 1
        await this.loadRows()
        if (this.isActiveStatus(this.currentTask.status)) this.startPolling()
      } catch (e) { this.handleError(e, '历史记录恢复失败') }
    },
    async loadRows () {
      if (!this.currentTask) return
      const taskId = this.currentTask.taskId; const requestId = this.activeRequestId
      this.rowsLoading = true
      try {
        const request = new CategoryTaskRowsRequest(); request.setTaskId(taskId); request.setCurrent(this.page); request.setSize(this.size)
        const response = this.ensureResponse(await categoryPredictServiceClient.pageTaskRows(request, this.grpcMetadata()))
        if (!this.currentTask || this.currentTask.taskId !== taskId || requestId !== this.activeRequestId) return
        this.showList = response.getRecordsList().map(this.rowObject)
        this.rowTotal = Number(response.getTotal())
      } catch (e) { this.handleError(e, '分类结果加载失败', false) } finally { this.rowsLoading = false }
    },
    startPolling () { this.stopPolling(); if (this.currentTask && this.isActiveStatus(this.currentTask.status)) this.pollTimer = window.setInterval(this.refreshCurrentTask, 2500) },
    stopPolling () { if (this.pollTimer) window.clearInterval(this.pollTimer); this.pollTimer = null },
    async refreshCurrentTask () {
      if (!this.currentTask) return
      const taskId = this.currentTask.taskId
      try {
        const request = new CategoryTaskRequest(); request.setTaskId(taskId)
        const response = this.ensureResponse(await categoryPredictServiceClient.getTask(request, this.grpcMetadata()))
        if (!this.currentTask || this.currentTask.taskId !== taskId) return
        this.currentTask = this.taskObject(response.getTask()); this.upsertHistory(this.currentTask)
        await this.loadRows()
        if (!this.isActiveStatus(this.currentTask.status)) { this.stopPolling(); await this.loadQuota() }
      } catch (e) { this.stopPolling(); this.handleError(e, '任务进度刷新失败', false) }
    },
    async exportToExcel () {
      if (!this.currentTask || this.exporting) return
      this.exporting = true
      const request = new CategoryTaskRequest(); request.setTaskId(this.currentTask.taskId)
      const chunks = []; let fileName = '分类结果.xlsx'; let contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      try {
        await new Promise((resolve, reject) => {
          const stream = categoryPredictStreamClient.exportTask(request, this.grpcMetadata())
          stream.on('data', chunk => { chunks.push(chunk.getData_asU8()); fileName = chunk.getFileName() || fileName; contentType = chunk.getContentType() || contentType })
          stream.on('end', resolve); stream.on('error', reject)
        })
        if (!chunks.length) throw new Error('导出结果为空')
        const blob = new Blob(chunks, { type: contentType })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.style.display = 'none'
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
      } catch (e) { this.handleError(e, '分类结果导出失败') } finally { this.exporting = false }
    },
    handlePageChange (page) { this.page = page; if (this.currentTask) this.loadRows() },
    handlePageSizeChange (size) { this.size = size; this.page = 1; if (this.currentTask) this.loadRows() },
    resetQuery () { this.stopPolling(); this.uploadId = ''; this.fileInfo = null; this.currentTask = null; this.columns = []; this.selColnum = ''; this.showList = []; this.rowTotal = 0; this.page = 1; this.clearUploadFiles() },
    upsertHistory (task, prepend) {
      const index = this.history.findIndex(item => item.taskId === task.taskId)
      if (index >= 0) this.history.splice(index, 1, { ...this.history[index], ...task })
      else if (prepend) { this.history.unshift(task); this.historyTotal += 1 }
    },
    taskObject (item) {
      return { taskId: item.getTaskId(), fileName: item.getFileName(), fileSize: item.getFileSize(), targetColumn: item.getTargetColumn(), columns: item.getColumnsList(), topCategoryCount: item.getTopCategoryCount(), maxLevel: item.getMaxLevel(), status: item.getStatus(), totalRows: item.getTotalRows(), processedRows: item.getProcessedRows(), successRows: item.getSuccessRows(), failedRows: item.getFailedRows(), progress: item.getProgress(), progressMessage: item.getProgressMessage(), errorMessage: item.getErrorMessage(), tier: item.getTier(), createTime: item.getCreateTime(), updateTime: item.getUpdateTime() }
    },
    rowObject (item) { let data = {}; try { data = JSON.parse(item.getDataJson() || '{}') } catch (e) {} return { ...data, rowNo: item.getRowNo(), sourceText: item.getSourceText(), category: item.getCategory(), firstL1: item.getFirstL1(), firstL2: item.getFirstL2(), firstL3: item.getFirstL3(), secondL1: item.getSecondL1(), secondL2: item.getSecondL2(), secondL3: item.getSecondL3(), aiL1: item.getAiL1(), aiL2: item.getAiL2(), aiL3: item.getAiL3(), finalL1: item.getFinalL1(), finalL2: item.getFinalL2(), finalL3: item.getFinalL3(), decisionSource: item.getDecisionSource(), aiConfidence: item.getAiConfidence(), status: item.getStatus(), errorMessage: item.getErrorMessage() } },
    previewRow (item) { let data = {}; try { data = JSON.parse(item.getDataJson() || '{}') } catch (e) {} return { ...data, rowNo: item.getRowNo(), status: 'PREVIEW', category: '' } },
    rowCategory (row) { if (row.status === 'FAILED') return row.errorMessage || '分类失败'; return row.category || (row.status === 'RUNNING' ? '分类中…' : '等待分类') },
    decisionText (source) { return { AI_OVERRIDE: 'AI改判', AI_CONFIRMED: '机标与AI一致', AI_WITH_MACHINE_FALLBACK: 'AI复核·部分机标兜底', MACHINE_FALLBACK: '机标结果' }[source] || source },
    decisionClass (source) { if (source === 'AI_OVERRIDE') return 'override'; if (source === 'AI_CONFIRMED') return 'confirmed'; return 'fallback' },
    rowStatusText (status) { return { QUEUED: '等待中', RUNNING: '分类中', SUCCEEDED: '完成', FAILED: '失败', CANCELLED: '已取消', PREVIEW: '预览' }[status] || status || '—' },
    statusText (status) { return { QUEUED: '等待分类', RUNNING: '正在分类', PAUSED: '已暂停', SUCCEEDED: '分类完成', PARTIAL_FAILED: '部分失败', FAILED: '任务失败', CANCELLED: '已取消' }[status] || status || '未知状态' },
    statusClass (status) { if (['SUCCEEDED'].includes(status)) return 'success'; if (ACTIVE_STATUSES.includes(status)) return 'processing'; if (['FAILED', 'PARTIAL_FAILED', 'CANCELLED'].includes(status)) return 'failed'; return '' },
    historySummary (task) { if (this.isActiveStatus(task.status)) return `已处理 ${task.processedRows || 0}/${task.totalRows || 0} 条`; if (task.status === 'PAUSED') return `已暂停 ${task.processedRows || 0}/${task.totalRows || 0} 条`; return `${this.statusText(task.status)} · ${task.successRows || 0} 条` },
    isActiveStatus (status) { return ACTIVE_STATUSES.includes(status) },
    grpcMetadata () { return { token: localStorage.getItem('token') || '' } },
    clearUploadFiles () { if (this.$refs.upload) this.$refs.upload.clearFiles() },
    readFile (file) { return new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = event => resolve(event.target.result); reader.onerror = reject; reader.readAsArrayBuffer(file) }) },
    uuid () { return `${Date.now()}-${Math.random().toString(16).slice(2)}` },
    handleError (error, fallback, notify = true) { const message = (error && (error.message || error.toString())) || fallback; if (/登录|token|NOT_FOUND/i.test(message)) this.openLogin(); if (notify) this.$message.error(message || fallback) }
  }
}
</script>

<style lang="less" scoped src="../assets/v2/css/trans/common.less"/>
<style scoped>
.category-tool-page,.category-tool-page>.group_1,.category-tool-page>.group_1>.box_1{min-height:100vh}.category-tool-page{width:100%;min-width:0;overflow:hidden;background:var(--bg-page)}.category-tool-page>.group_1>.box_1{justify-content:flex-start}.group_3,.new-container,.box_5{display:flex;flex:1 1 auto;width:100%;min-width:0;min-height:0;overflow:hidden}.new-container{flex-direction:column;background:var(--bg-card)}
.history-scroll::-webkit-scrollbar{width:5px}.history-scroll::-webkit-scrollbar-thumb{border-radius:999px;background:var(--primary-alpha-24)}
.rag-main{flex:1;min-width:0;min-height:0;padding:0;overflow:hidden}.category-layout{display:grid;grid-template-columns:minmax(0,1fr) 230px;height:100%;min-height:0}.category-tool{display:flex;flex-direction:column;gap:14px;min-width:0;min-height:0;padding:20px;overflow:auto}.toolbar,.info{display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding:14px 16px;border:1px solid var(--border);border-radius:var(--radius-xl);background:var(--bg-card)}.toolbar-select{width:130px}.hierarchy-chip{padding:7px 10px;border-radius:999px;background:#eef4ff;color:#476b9d;font-size:12px}.button-right{display:flex;flex:1;justify-content:flex-end;gap:8px}.toolbar /deep/ .el-button:not(.el-button--text){display:inline-flex;align-items:center;justify-content:center;min-width:76px;height:40px;margin:0;padding:0 16px;border:1px solid var(--border);border-radius:var(--radius-md);background:var(--bg-card);color:var(--foreground-secondary);font-size:14px;transition:border-color .2s,background .2s,color .2s}.toolbar /deep/ .el-button--primary:not(.is-plain){border-color:var(--primary);background:var(--primary);color:#fff}.toolbar /deep/ .el-button--primary:not(.is-plain):hover,.toolbar /deep/ .el-button--primary:not(.is-plain):focus{border-color:var(--primary-dark);background:var(--primary-dark);color:#fff}.toolbar /deep/ .el-button--default:not(.is-disabled):hover,.toolbar /deep/ .el-button--default:not(.is-disabled):focus{border-color:var(--primary-alpha-24);color:var(--primary-dark)}.toolbar /deep/ .el-button.is-disabled,.toolbar /deep/ .el-button.is-disabled:hover,.toolbar /deep/ .el-button.is-disabled:focus{border-color:var(--border);background:var(--bg-subtle);color:var(--disabled-text)}.quota-chip{display:flex;align-items:center;gap:5px;padding:7px 10px;border-radius:999px;background:var(--primary-alpha-08);color:var(--primary-dark);font-size:12px}.quota-chip.unlimited{background:#e9f8f1;color:#11845f}.info-item,.info-progress{display:flex;align-items:center;gap:6px;min-width:0;color:var(--muted-foreground);font-size:13px}.info-item span{max-width:310px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.curr-count{color:var(--primary);font-weight:700}.info-progress-bar{flex:1;min-width:150px;height:6px;border-radius:999px;background:var(--bg-subtle);overflow:hidden}.info-progress-bar .bar{height:100%;background:var(--primary-gradient);transition:width .3s}
.excel-upload{display:block;width:100%}.excel-upload /deep/ .el-upload{display:block;width:100%}.excel-upload /deep/ .el-upload-dragger{display:block;width:100%;height:auto;padding:0;border:1px dashed #b8d8cc;border-radius:var(--radius-xl);background:linear-gradient(135deg,#fbfffd 0%,#f4fbf8 100%);overflow:hidden;transition:border-color .2s,background .2s,box-shadow .2s}.excel-upload /deep/ .el-upload-dragger:hover{border-color:var(--primary);background:#f2fbf7;box-shadow:0 5px 16px rgba(22,157,112,.08)}.excel-upload /deep/ .el-upload-dragger.is-dragover{border:2px dashed var(--primary);background:#edf9f4}.excel-upload /deep/ .el-upload.is-disabled .el-upload-dragger{cursor:wait;opacity:.72}.upload-box-content{display:flex;align-items:center;gap:14px;min-height:76px;padding:12px 18px;text-align:left}.upload-box-icon{display:grid;place-items:center;flex:0 0 46px;height:46px;border-radius:12px;background:#e3f5ed;color:var(--primary);font-size:23px}.upload-box-copy{display:flex;flex:1;min-width:0;flex-direction:column;gap:5px}.upload-box-copy strong{overflow:hidden;color:var(--foreground);font-size:15px;line-height:20px;text-overflow:ellipsis;white-space:nowrap}.upload-box-copy small{color:var(--muted-foreground);font-size:12px;line-height:18px}.upload-box-action{flex:0 0 auto;padding:7px 13px;border:1px solid #a8dcca;border-radius:999px;background:#fff;color:var(--primary-dark);font-size:12px;font-weight:600}
.table-wrap{min-height:360px;border:1px solid var(--border);border-radius:var(--radius-xl);background:var(--bg-card);overflow:hidden}.result-table /deep/ .el-table__header th{background:var(--bg-subtle);color:var(--foreground-secondary)}.cell-text{color:var(--foreground-secondary);font-size:13px}.cell-text.is-empty{color:var(--disabled-text)}.category-result-cell{display:flex;align-items:center;gap:7px;min-width:0}.category-result-cell>.cell-text{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.decision-source{flex:none;padding:2px 6px;border-radius:8px;background:#edf2f6;color:#71808e;font-size:10px}.decision-source.override{background:#fff1e7;color:#c66824}.decision-source.confirmed{background:#e9f7f1;color:#168a66}.secondary-result{color:#71808e}.row-status{font-size:12px;color:var(--muted-foreground)}.row-status.success{color:#168a66}.row-status.processing{color:#2387d9}.row-status.failed{color:#c45c58}.empty-state{min-height:360px;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#9ba5b2}.empty-state i{font-size:42px;color:#d7dee6}.empty-state h3{margin:16px 0 6px}.empty-state p{margin:0;font-size:13px}.pagination-wrap{display:flex;justify-content:center;padding:4px 0 8px}
.history-panel{min-width:0;min-height:0;border-left:1px solid var(--border);background:#fff}.history-scroll{height:100%;box-sizing:border-box;overflow-y:auto;padding:12px 10px 24px;scrollbar-width:thin;scrollbar-color:var(--primary-alpha-24) transparent}.history-item{display:flex;align-items:center;gap:10px;width:100%;min-height:80px;margin:0 0 14px;padding:9px;border:1px solid transparent;border-radius:8px;background:#fff;color:inherit;cursor:pointer;text-align:left}.history-item:hover{background:#f6faf8}.history-item.active{border-color:#93d9bd;background:#eff9f5;box-shadow:0 2px 8px rgba(23,183,123,.08)}.history-thumbnail{position:relative;display:grid;place-items:center;flex:0 0 72px;height:58px;border-radius:5px;background:#e9f1f7;color:#4da4e8;font-size:28px}.thumbnail-progress{position:absolute;right:5px;bottom:5px;left:5px;height:3px;border-radius:999px;background:#d9e1e8;overflow:hidden}.thumbnail-progress span{display:block;height:100%;background:var(--primary)}.history-info{display:flex;flex:1;min-width:0;flex-direction:column;gap:5px}.history-info strong,.history-info span,.history-info time{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.history-info strong{font-size:13px}.history-status,.history-info time{color:#8c96a2;font-size:11px}.history-status.success{color:#168a66}.history-status.processing{color:#158b67}.history-status.failed{color:#bc6b66}.history-state{min-height:190px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#a1aab5;font-size:12px}.history-state i{font-size:24px}.history-more,.history-end{text-align:center;color:#b0b7bf;font-size:11px;padding:10px}.translate-site-footer{position:relative;z-index:20;display:block;flex:0 0 64px;min-height:64px;margin-top:0}
@media(min-width:769px){.category-tool-page,.category-tool-page>.group_1,.category-tool-page>.group_1>.box_1{height:100vh;min-height:640px}}
@media(max-width:900px){.category-layout{grid-template-columns:1fr}.history-panel{min-height:360px;border-left:0;border-top:1px solid var(--border)}.history-scroll{max-height:420px}}
@media(max-width:768px){.category-tool{padding:12px}.upload-box-content{min-height:70px;padding:10px 12px}.upload-box-icon{flex-basis:40px;height:40px;font-size:20px}.upload-box-action{display:none}.upload-box-copy strong{font-size:14px}.upload-box-copy small{white-space:normal}.button-right{flex-basis:100%}}
</style>
